import { useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { getSharedFormularioRespuesta } from "@/lib/api";
import { toast } from "sonner";
import { openAdjuntoEnNuevaPestana } from "@/lib/openAdjuntoEnNuevaPestana";

function flattenBlocks(blocks) {
  const list = [];
  const walk = (arr) => {
    for (const b of arr || []) {
      if (!b || typeof b !== "object") continue;
      list.push(b);
      if (b.tipo === "columnas") {
        const cols = Array.isArray(b.columnas_bloques) ? b.columnas_bloques : [];
        cols.forEach((col) => walk(Array.isArray(col) ? col : []));
      }
    }
  };
  walk(Array.isArray(blocks) ? blocks : []);
  return list;
}

function RespuestaConUrls({ value }) {
  if (value == null) return null;
  const text = String(value);
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return (
    <span>
      {parts.map((part, i) =>
        urlRegex.test(part) ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-2 break-all"
          >
            {part}
          </a>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
}

function parseRespuestaArchivos(value) {
  const getAdjuntoUrl = (f) => {
    if (!f || typeof f !== "object") return null;
    if (typeof f.url === "string" && f.url) return f.url;
    if (typeof f.data_url === "string" && f.data_url) return f.data_url;
    return null;
  };
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((f) => getAdjuntoUrl(f));
  } catch (_) {
    return [];
  }
}

function RespuestaArchivos({ value }) {
  const files = parseRespuestaArchivos(value);
  if (!files.length) return <span className="text-muted-foreground">Sin archivos</span>;
  return (
    <div className="flex flex-wrap gap-2">
      {files.map((f, i) => {
        const name = f.name || `archivo-${i + 1}`;
        const url = f.url || f.data_url;
        const isImage = typeof f.type === "string" && f.type.startsWith("image/");
        return (
          <button
            key={`${name}-${i}`}
            type="button"
            onClick={() => openAdjuntoEnNuevaPestana(url)}
            className="inline-flex items-center gap-2 rounded-md border bg-muted/50 px-2.5 py-1.5 text-xs hover:bg-muted text-left cursor-pointer"
          >
            {isImage ? <img src={url} alt={name} className="h-8 w-8 rounded object-cover border pointer-events-none" /> : null}
            <span className="max-w-[240px] truncate">{name}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function RespuestaCompartidaPage() {
  const { shareId } = useParams();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!shareId || !password.trim() || loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await getSharedFormularioRespuesta(shareId, password.trim());
      setData(res);
    } catch (err) {
      const msg = err.response?.data?.detail || "Contraseña incorrecta o enlace no válido";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!shareId) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background px-4">
        <p className="text-sm text-muted-foreground">Enlace no válido.</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <h1 className="text-xl font-semibold text-center">Ver respuesta compartida</h1>
              <p className="text-sm text-muted-foreground text-center">
                Introduce la contraseña para poder ver la respuesta.
              </p>
              <form onSubmit={handleSubmit} className="space-y-3">
                <Input
                  type="password"
                  autoComplete="off"
                  placeholder="Contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {error && <p className="text-xs text-destructive text-center">{error}</p>}
                <Button type="submit" className="w-full gap-2" disabled={loading || !password.trim()}>
                  {loading && <Loader2 className="w-4 h-4 animate-spin" aria-hidden />}
                  Ver respuesta
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const respuestas = data.respuestas || {};
  const bloques = flattenBlocks(Array.isArray(data.bloques) ? data.bloques : []);
  const blockIdToTitle = {};
  const blockIdToType = {};
  bloques.forEach((b) => {
    if (b && b.id) {
      blockIdToTitle[b.id] = (b.titulo || "").trim() || b.id;
      blockIdToType[b.id] = b.tipo || "";
    }
  });

  return (
    <div className="min-h-dvh flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-2xl space-y-6">
        <h1 className="text-2xl font-semibold text-center">
          {data.formulario_titulo || "Formulario"}
        </h1>
        <Card className="mx-auto max-w-xl w-full">
          <CardContent className="pt-6 space-y-4">
            {Object.keys(respuestas).length === 0 && (
              <p className="text-sm text-muted-foreground text-center">Sin respuestas.</p>
            )}
            {Object.entries(respuestas).map(([blockId, value]) => (
              <div key={blockId} className="flex flex-col gap-1">
                <dt className="text-xs text-muted-foreground font-medium text-left">
                  {blockIdToTitle[blockId] || blockId}
                </dt>
                <dd className="text-sm break-words text-left">
                  {blockIdToType[blockId] === "archivo" ? (
                    <RespuestaArchivos value={value} />
                  ) : blockIdToType[blockId] === "consentimiento_privacidad" ? (
                    <span className={value ? "text-foreground" : "text-muted-foreground"}>
                      {value ? "Aceptada" : "No aceptada"}
                    </span>
                  ) : (
                    <RespuestaConUrls value={value} />
                  )}
                </dd>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

